import { BookingResponseDto, BOOKING_STATUS_LABELS } from '../types/booking';
import { bookingApi } from './bookingApi';

// The backend does not expose a dedicated reports/export endpoint, so reports are
// aggregated client-side from GET /api/AdminBooking and exported in-browser.
export interface BookingReportFilters {
  dateFrom?: string;
  dateTo?: string;
}

export interface BookingReportResult {
  totalBookings: number;
  completedBookings: number;
  cancelledBookings: number;
  totalRevenue: number;
  bookingsByService: { serviceName: string; count: number }[];
  bookingsByMonth: { month: string; count: number }[];
  bookingsByStatus: { status: string; count: number }[];
  bookings: BookingResponseDto[];
}

const withinRange = (booking: BookingResponseDto, filters?: BookingReportFilters) => {
  const date = booking.bookingDate.slice(0, 10);
  if (filters?.dateFrom && date < filters.dateFrom) return false;
  if (filters?.dateTo && date > filters.dateTo) return false;
  return true;
};

export const bookingReportsApi = {
  getReport: async (filters?: BookingReportFilters): Promise<BookingReportResult> => {
    const res = await bookingApi.getBookings();
    const bookings = res.data.filter((b) => withinRange(b, filters));

    const byService = new Map<string, number>();
    const byMonth = new Map<string, number>();
    const byStatus = new Map<number, number>();

    bookings.forEach((b) => {
      byService.set(b.serviceName, (byService.get(b.serviceName) ?? 0) + 1);
      const month = new Date(b.bookingDate).toLocaleString('default', { month: 'short', year: 'numeric' });
      byMonth.set(month, (byMonth.get(month) ?? 0) + 1);
      byStatus.set(b.status, (byStatus.get(b.status) ?? 0) + 1);
    });

    return {
      totalBookings: bookings.length,
      completedBookings: byStatus.get(2) ?? 0,
      cancelledBookings: byStatus.get(3) ?? 0,
      totalRevenue: 0, // Price is only known via the related BookingService, not returned on BookingResponseDto.
      bookingsByService: Array.from(byService, ([serviceName, count]) => ({ serviceName, count })),
      bookingsByMonth: Array.from(byMonth, ([month, count]) => ({ month, count })),
      bookingsByStatus: Array.from(byStatus, ([status, count]) => ({ status: BOOKING_STATUS_LABELS[status as keyof typeof BOOKING_STATUS_LABELS], count })),
      bookings,
    };
  },
};

const toCsvValue = (value: string | number) => `"${String(value).replace(/"/g, '""')}"`;

export const exportBookingsToCsv = (bookings: BookingResponseDto[]): Blob => {
  const header = ['Reference', 'Customer', 'Email', 'Service', 'Date', 'Status'];
  const rows = bookings.map((b) => [
    b.bookingReference,
    `${b.customerFirstName} ${b.customerLastName}`,
    b.customerEmail,
    b.serviceName,
    b.bookingDate,
    BOOKING_STATUS_LABELS[b.status],
  ]);
  const csv = [header, ...rows].map((row) => row.map(toCsvValue).join(',')).join('\n');
  return new Blob([csv], { type: 'text/csv;charset=utf-8;' });
};

